/**
 * OfferRequestDialog Component
 * Two-step marketing dialog shown when user clicks "Unverbindliches Angebot anfragen".
 * Step 1: Info page with benefits.
 * Step 2: Request form with email recipients — submits to backend which sends emails.
 */

import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { FileText, Mail, X, ChevronDown, ArrowLeft, Plus, Trash2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOfferRequestDialogStore } from '@/stores/offerRequestDialogStore';
import { useAuth } from '@/hooks/useAuth';
import { analysesService } from '@/services/api/analyses';

export function OfferRequestDialog() {
  const { t } = useTranslation();
  const { isOpen, requestId, close } = useOfferRequestDialogStore();
  const { user } = useAuth();
  const [step, setStep] = useState<'info' | 'request' | 'success'>('info');
  const [ccEmails, setCcEmails] = useState<string[]>([]);

  const requestMutation = useMutation({
    mutationFn: (ccEmails: string[]) =>
      analysesService.requestPremiumAnalysis(
        ccEmails.filter((e) => e.trim() !== ''),
        requestId
      ),
    onSuccess: () => {
      setStep('success');
    },
  });

  const handleClose = () => {
    close();
    setStep('info');
    setCcEmails([]);
    requestMutation.reset();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requestMutation.mutate(ccEmails);
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          aria-describedby="offer-request-dialog-description"
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] border border-[var(--glass-border)] bg-white p-6 shadow-[var(--glass-shadow)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ width: 'min(28rem, calc(100vw - 2rem))', maxHeight: '90dvh', overflowY: 'auto' }}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-start)] focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* ── Step 1: Info ────────────────────────────────────────── */}
          {step === 'info' && (
            <>
              {/* Header */}
              <div className="flex flex-col items-center text-center space-y-1.5">
                <div className="mb-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center">
                    <FileText className="h-7 w-7 text-[var(--color-primary-start)]" />
                  </div>
                </div>
                <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                  {t('offerRequestDialog.title')}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description id="offer-request-dialog-description" className="text-sm text-[var(--color-charcoal)]/70 mt-2 leading-relaxed">
                  {t('offerRequestDialog.description')}
                </DialogPrimitive.Description>
              </div>

              {/* Benefits */}
              <div className="mt-4">
                <ul className="space-y-2 text-sm text-[var(--color-charcoal)]/80">
                  {[1, 2, 3].map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                      <span>{t(`offerRequestDialog.benefit${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Collapsible "Erfahren Sie mehr" */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById('offer-request-details');
                    el?.toggleAttribute('data-open');
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary-start)] hover:opacity-80 transition-opacity mx-auto"
                >
                  <span>{t('offerRequestDialog.learnMore')}</span>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-open]_&]:rotate-180" />
                </button>
                <div
                  id="offer-request-details"
                  className="grid transition-all duration-300 ease-in-out grid-rows-[0fr] opacity-0 [&[data-open]]:grid-rows-[1fr] [&[data-open]]:opacity-100"
                >
                  <div className="overflow-hidden">
                    <ul className="mt-3 space-y-2 text-sm text-[var(--color-charcoal)]/80 text-left">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>{t(`offerRequestDialog.detail${i}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="mt-6 pt-4 border-t border-[var(--glass-border)]">
                <Button className="w-full btn-primary" onClick={() => setStep('request')}>
                  {t('offerRequestDialog.cta')}
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: Request form ───────────────────────────────── */}
          {step === 'request' && (
            <form onSubmit={handleSubmit}>
              {/* Back button */}
              <button
                type="button"
                onClick={() => setStep('info')}
                disabled={requestMutation.isPending}
                className="flex items-center gap-1 text-sm text-[var(--color-charcoal)]/60 hover:text-[var(--color-charcoal)] transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('offerRequestDialog.back')}</span>
              </button>

              {/* Header */}
              <div className="flex flex-col items-center text-center space-y-1.5">
                <div className="mb-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center">
                    <Mail className="h-7 w-7 text-[var(--color-primary-start)]" />
                  </div>
                </div>
                <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                  {t('offerRequestDialog.requestTitle')}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-sm text-[var(--color-charcoal)]/70 mt-2 leading-relaxed">
                  {t('offerRequestDialog.requestDescription')}
                </DialogPrimitive.Description>
              </div>

              {/* User email (read-only) */}
              <div className="mt-4 space-y-2">
                <Label className="text-[var(--color-charcoal)] font-medium">
                  {t('offerRequestDialog.recipientLabel')}
                </Label>
                <Input
                  type="email"
                  value={user?.email ?? ''}
                  disabled
                  className="bg-[#f5f5f7] border-[var(--glass-border)] text-[var(--color-charcoal)]/70 cursor-not-allowed"
                />
              </div>

              {/* CC Emails */}
              <div className="mt-4 space-y-3">
                <Label className="text-[var(--color-charcoal)] font-medium">
                  {t('offerRequestDialog.ccLabel')}
                </Label>
                <div id="offer-request-cc-list" className="space-y-2">
                  {ccEmails.map((email, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        required
                        placeholder="name@firma.de"
                        value={email}
                        disabled={requestMutation.isPending}
                        onChange={(e) => {
                          const updated = [...ccEmails];
                          updated[index] = e.target.value;
                          setCcEmails(updated);
                        }}
                        className="bg-white/50 border-[var(--glass-border)] focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={requestMutation.isPending}
                        onClick={() => setCcEmails(ccEmails.filter((_, i) => i !== index))}
                        className="shrink-0 text-[var(--color-charcoal)]/40 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={requestMutation.isPending}
                  onClick={() => {
                    setCcEmails([...ccEmails, '']);
                    requestAnimationFrame(() => {
                      const inputs = document.querySelectorAll<HTMLInputElement>('#offer-request-cc-list input[type="email"]');
                      inputs[inputs.length - 1]?.focus();
                    });
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary-start)] hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  <span>{t('offerRequestDialog.addRecipient')}</span>
                </button>
              </div>

              {/* Error message */}
              {requestMutation.isError && (
                <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{t('offerRequestDialog.error')}</span>
                </div>
              )}

              {/* Action */}
              <div className="mt-6 pt-4 border-t border-[var(--glass-border)]">
                <Button type="submit" disabled={requestMutation.isPending} className="w-full btn-primary gap-2">
                  {requestMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('offerRequestDialog.submitting')}
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      {t('offerRequestDialog.submitRequest')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* ── Step 3: Success ─────────────────────────────────────── */}
          {step === 'success' && (
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </div>
              <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                {t('offerRequestDialog.successTitle')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-[var(--color-charcoal)]/70 leading-relaxed">
                {t('offerRequestDialog.successDescription')}
              </DialogPrimitive.Description>
              <div className="pt-4 w-full">
                <Button className="w-full btn-primary" onClick={handleClose}>
                  {t('offerRequestDialog.close')}
                </Button>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
